import { useParams } from 'react-router-dom';

/** A route rendered without a param its path template is supposed to supply. */
export class MissingRouteParamError extends Error {
  constructor(param: string) {
    super(`Missing required route param: ${param}`);
    this.name = 'MissingRouteParamError';
  }
}

/**
 * Pick `keys` from route params, throwing when any is missing or empty. Pure
 * so it can be tested without a router.
 */
export function pickRequiredParams<const K extends string>(
  params: Readonly<Partial<Record<string, string>>>,
  keys: readonly K[]
): Readonly<Record<K, string>> {
  const picked: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = params[key];
    if (value === undefined || value === '') {
      throw new MissingRouteParamError(key);
    }
    picked[key] = value;
  }
  // Every key in `keys` was assigned above or the loop threw.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- all keys populated by the loop
  return picked as Record<K, string>;
}

/**
 * Route params the current route's path template always supplies. Use only on
 * pages mounted exclusively under templates that contain every key: a missing
 * param throws `MissingRouteParamError`, which the router's `errorElement`
 * renders, instead of flowing through the page as a non-null-asserted
 * `undefined`.
 */
export function useRequiredParams<const K extends string>(
  keys: readonly K[]
): Readonly<Record<K, string>> {
  return pickRequiredParams(useParams(), keys);
}
